import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { ImageWithFallback } from '@/modules/common/components/ImageWithFallback';
import {
  ITaxBenefitsProvider,
  ITaxBenefitsProviderWithLinkStatus,
  TaxBenefitsProviderCompanyLinkStatus,
} from '@/types/tax-benefits-provider';
import { yupResolver } from '@hookform/resolvers/yup';
import { AccountBalanceOutlined } from '@mui/icons-material';
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useCompanyMutation } from '../hooks/useCompanyMutation';
import { useGetTaxBenefitProviders } from '../hooks/useTaxBenefitProviders';
import { TaxBenefitsReviewMode } from '../types';
import { CancelPartnershipConfirmationModal } from './CancelPartnershipConfirmationModal';
import { TaxBenefitsSelectorModal } from './TaxBenefitsSelectorModal';

export type TaxBenefitsFormData = {
  taxBenefitsReviewMode: TaxBenefitsReviewMode;
  taxBenefitsProvider?: ITaxBenefitsProviderWithLinkStatus | null;
};

type CompanyTaxBenefitsFormProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onSubmit'
> & {
  initialData: TaxBenefitsFormData;
  onSubmit: (data: TaxBenefitsFormData) => Promise<void>;
  isLoading?: boolean;
  taxBenefitsProviders: ITaxBenefitsProvider[];
};

const FORM_SCHEMA = yup.object().shape({
  taxBenefitsReviewMode: yup
    .string()
    .oneOf(Object.values(TaxBenefitsReviewMode), 'Please select a valid mode')
    .required('Please select a mode'),
  taxBenefitsProvider: yup
    .mixed()
    .when('taxBenefitsReviewMode', ([taxBenefitsReviewMode]) => {
      if (taxBenefitsReviewMode === TaxBenefitsReviewMode.Independent) {
        return yup.mixed().nullable().oneOf([null]);
      }
      return yup.object().required('Please select a provider');
    }),
});

export function CompanyTaxBenefitsForm({
  initialData,
  onSubmit,
  isLoading = false,
  taxBenefitsProviders,
  ...props
}: CompanyTaxBenefitsFormProps) {
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const {
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    setValue,
    reset,
    watch,
  } = useForm<TaxBenefitsFormData>({
    disabled: isLoading,
    resolver: yupResolver(FORM_SCHEMA) as Resolver<TaxBenefitsFormData>,
    values: initialData,
  });

  const taxBenefitsReviewMode = watch('taxBenefitsReviewMode');
  const taxBenefitsProvider = watch('taxBenefitsProvider');

  const onCancelPartnership = async () => {
    setValue('taxBenefitsProvider', null, { shouldDirty: true });
    setValue('taxBenefitsReviewMode', TaxBenefitsReviewMode.Independent, {
      shouldDirty: true,
    });
  };

  const onProviderSelect = async (provider?: ITaxBenefitsProvider) => {
    if (!provider) {
      setValue('taxBenefitsProvider', null, { shouldDirty: true });
      return;
    }

    return setValue(
      'taxBenefitsProvider',
      provider as ITaxBenefitsProviderWithLinkStatus,
      { shouldDirty: true },
    );
  };

  useEffect(() => {
    if (taxBenefitsReviewMode === TaxBenefitsReviewMode.Independent) {
      setValue('taxBenefitsProvider', null, { shouldDirty: true });
    }
  }, [taxBenefitsReviewMode, setValue]);

  return (
    <div {...props} className={`${props.className}`}>
      <div>
        <Typography variant="h4" component="h2" className="mb-4">
          Tax Benefits
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <RadioGroup
            value={taxBenefitsReviewMode}
            onChange={e => {
              const reviewMode = e.target.value as TaxBenefitsReviewMode;

              setValue('taxBenefitsReviewMode', reviewMode, {
                shouldDirty: true,
              });

              if (
                reviewMode === TaxBenefitsReviewMode.Provider &&
                !taxBenefitsProvider
              ) {
                setIsSelectionModalOpen(true);
              }
            }}
          >
            <FormControlLabel
              value={TaxBenefitsReviewMode.Independent}
              control={<Radio />}
              label="Approve tax benefits independently."
            />
            <FormControlLabel
              value={TaxBenefitsReviewMode.Provider}
              control={<Radio disabled={taxBenefitsProviders?.length === 0} />}
              label="Select a provider to authorize the tax benefits."
              disabled={taxBenefitsProviders?.length === 0}
            />
          </RadioGroup>

          {taxBenefitsReviewMode === TaxBenefitsReviewMode.Provider && (
            <div className={`mt-8 bg-light-gray p-6 rounded-xl`}>
              <h3 className="text-lg font-medium leading-8 text-mydra-black">
                Tax benefits provider
              </h3>
              <p className="mt-2 text-sm text-mydra-black">
                Select your tax benefits provider - this provider will handle
                your employees tax benefits applications on your behalf.
              </p>

              {taxBenefitsProvider && (
                <div className="flex my-6 rounded-xl">
                  <div className="flex flex-1 gap-8">
                    <button
                      type="button"
                      onClick={() => setIsSelectionModalOpen(true)}
                      className="flex flex-shrink-0 gap-2 items-center"
                    >
                      {taxBenefitsProvider.logoUrl ? (
                        <ImageWithFallback
                          src={taxBenefitsProvider.logoUrl}
                          alt={taxBenefitsProvider.name}
                          className="w-16 h-16 bg-white bg-cover rounded-lg aspect-square"
                          fallback={
                            <AccountBalanceOutlined className="!w-16 !h-16 text-mydra-medium-gray" />
                          }
                        />
                      ) : (
                        <AccountBalanceOutlined className="!w-16 !h-16 text-mydra-medium-gray" />
                      )}
                      <p className="text-sm font-bold">
                        {taxBenefitsProvider.name}
                      </p>
                    </button>
                    <dl className="flex flex-wrap gap-8 items-center text-left">
                      {taxBenefitsProvider.legalName && (
                        <div className="flex flex-col text-sm">
                          <dt className="text-sm font-bold">Legal name</dt>
                          <dd className="text-sm">
                            {taxBenefitsProvider.legalName}
                          </dd>
                        </div>
                      )}
                      {taxBenefitsProvider.address && (
                        <div className="flex flex-col text-sm">
                          <dt className="text-sm font-bold">Address</dt>
                          <dd className="text-sm">
                            {taxBenefitsProvider.address}
                          </dd>
                        </div>
                      )}
                      <div className="flex flex-col text-sm">
                        <dt className="text-sm font-bold">Status</dt>
                        <dd className="text-sm capitalize">
                          {taxBenefitsProvider.status?.toLowerCase() ??
                            '(unknown)'}
                        </dd>
                      </div>
                    </dl>
                    <div className="flex flex-col flex-1 items-end">
                      <Button
                        type="button"
                        loading={isLoading}
                        disabled={!taxBenefitsProvider.status}
                        size="small"
                        className="my-auto"
                        onClick={() => setIsCancelModalOpen(true)}
                      >
                        Cancel{' '}
                        {taxBenefitsProvider.status ===
                          TaxBenefitsProviderCompanyLinkStatus.Invited ||
                        !taxBenefitsProvider.status
                          ? 'invitation'
                          : 'partnership'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {errors.taxBenefitsProvider && (
            <p className="text-sm text-red-500">
              {errors.taxBenefitsProvider.message}
            </p>
          )}

          {isDirty && (
            <div className="flex gap-3 mt-8">
              <Button
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={e => {
                  e.preventDefault();
                  reset();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          )}
        </form>
      </div>
      <TaxBenefitsSelectorModal
        taxBenefitsProviders={taxBenefitsProviders}
        open={isSelectionModalOpen}
        onClose={(provider?: ITaxBenefitsProvider) => {
          onProviderSelect(provider);
          setIsSelectionModalOpen(false);
          if (!provider) {
            setValue(
              'taxBenefitsReviewMode',
              TaxBenefitsReviewMode.Independent,
            );
          }
        }}
      />
      {taxBenefitsProvider && (
        <CancelPartnershipConfirmationModal
          open={isCancelModalOpen}
          status={taxBenefitsProvider.status}
          onClose={async (cancel: boolean) => {
            if (cancel) {
              await onCancelPartnership?.();
            }
            setIsCancelModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

type CompanyTaxBenefitsProviderProps = React.HTMLAttributes<HTMLDivElement>;

export function CompanyTaxBenefitsProvider(
  props: CompanyTaxBenefitsProviderProps,
) {
  const {
    employer: {
      taxBenefitsProvider: companyTaxBenefitsProvider = null,
      _id: employerId = '',
      settings: companySettings = {},
    } = {},
    isLoading: isLoadingUserContext,
  } = useCurrentUserContext() ?? {};

  const { skipTaxBenefitsProviderValidation = null } = companySettings;

  const {
    isLoading: isLoadingTaxBenefitsProviders,
    data: { data: taxBenefitsProviders = [] } = {},
  } = useGetTaxBenefitProviders({}, { page: 1, limit: 5000 });

  const isLoading = isLoadingTaxBenefitsProviders || isLoadingUserContext;

  const { mutateAsync: updateEmployer } = useCompanyMutation(employerId);

  const handleSubmit = async (data: TaxBenefitsFormData) => {
    await updateEmployer({
      id: employerId,
      updatedData: {
        taxBenefitsProvider:
          data.taxBenefitsReviewMode === TaxBenefitsReviewMode.Provider
            ? data.taxBenefitsProvider?._id ?? null
            : null,
        settings: {
          ...companySettings,
          skipTaxBenefitsProviderValidation:
            data.taxBenefitsReviewMode === TaxBenefitsReviewMode.Independent,
        },
      },
    });
  };

  return (
    <CompanyTaxBenefitsForm
      {...props}
      initialData={{
        taxBenefitsProvider: companyTaxBenefitsProvider,
        taxBenefitsReviewMode: skipTaxBenefitsProviderValidation
          ? TaxBenefitsReviewMode.Independent
          : TaxBenefitsReviewMode.Provider,
      }}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      taxBenefitsProviders={taxBenefitsProviders}
    />
  );
}
